// Choosing what an outside AI (or a personal access token) may reach: servers, then per server which apps (all of
// them, now and future, or some), read vs read and act, whether it may build apps, and messages: channel messages
// (none, read, or read and send; all channels or some) and DMs (none or read, never send; everyone's or some people's). Used by the consent screen (/oauth/authorize) and by
// Settings → Connected apps & AI (editing a connection, making a token).
import type { ConnectorAccess, ConnectorServerChoice, DmAccess, GrantServerScope, MessageAccess } from "@bloom/shared";
import { initials } from "./ui.tsx";
import "./connector.css";

/** The starting choice: what the member allowed before, else only their one server (if they have just one). */
export function initialScopes(servers: ConnectorServerChoice[], current: GrantServerScope[] | null, access: ConnectorAccess = "read"): GrantServerScope[] {
  if (current) return current.filter((c) => servers.some((s) => s.id === c.serverId));
  return servers.length === 1 ? [{ serverId: servers[0]!.id, apps: "all", access }] : [];
}

/** "Read and act · all apps" */
export function describeScope(s: GrantServerScope, apps?: ConnectorServerChoice["apps"]): string {
  const which = s.apps === "all" ? "all apps" : s.apps.map((slug) => apps?.find((a) => a.slug === slug)?.name ?? slug).join(", ");
  const messages = s.messages === "send" ? " · reads and sends messages" : s.messages === "read" ? " · reads messages" : "";
  const dms = s.dms === "read" ? " · reads DMs" : "";
  return `${s.access === "act" ? "Read and act" : "Read only"} · ${which}${s.build ? " · builds apps" : ""}${messages}${dms}`;
}

/** "All of them" or a list of some, with checkboxes: channels for messages, people for DMs. */
function SomeOrAll({ name, all, some, items, value, onChange }: { name: string; all: string; some: string; items: { id: string; name: string }[]; value: "all" | string[]; onChange: (v: "all" | string[]) => void }) {
  return (
    <div className="cs-apps">
      <label className="cs-check">
        <input type="radio" name={name} checked={value === "all"} onChange={() => onChange("all")} />
        {all}
      </label>
      <label className="cs-check">
        <input type="radio" name={name} checked={value !== "all"} onChange={() => onChange(items.slice(0, 1).map((i) => i.id))} disabled={!items.length} />
        {some}
      </label>
      {value !== "all" && (
        <ul className="cs-app-list">
          {items.map((i) => {
            const on = value.includes(i.id);
            return (
              <li key={i.id}>
                <label className="cs-check cs-app">
                  <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== i.id) : [...value, i.id])} />
                  <span className="cs-app-name">{i.name}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ScopePicker({ servers, value, onChange, idPrefix = "cs" }: { servers: ConnectorServerChoice[]; value: GrantServerScope[]; onChange: (v: GrantServerScope[]) => void; idPrefix?: string }) {
  if (!servers.length) return <p className="muted cs-empty">You're not in any servers yet.</p>;
  const set = (serverId: string, patch: Partial<GrantServerScope> | null) => {
    const rest = value.filter((v) => v.serverId !== serverId);
    if (patch === null) return onChange(rest);
    const cur = value.find((v) => v.serverId === serverId) ?? { serverId, apps: "all" as const, access: "read" as const };
    // Keep the servers' order.
    const next = [...rest, { ...cur, ...patch }];
    onChange(servers.map((s) => next.find((n) => n.serverId === s.id)).filter((x): x is GrantServerScope => !!x));
  };
  return (
    <ul className="cs-servers">
      {servers.map((s) => {
        const v = value.find((x) => x.serverId === s.id);
        const id = `${idPrefix}-${s.id}`;
        return (
          <li key={s.id} className={`cs-server${v ? " on" : ""}`} data-server={s.name}>
            <label className="cs-server-head" htmlFor={id}>
              <input id={id} type="checkbox" checked={!!v} onChange={(e) => set(s.id, e.target.checked ? {} : null)} />
              <span className="cs-badge" style={{ background: s.color }} aria-hidden="true">{initials(s.name)}</span>
              <span className="cs-server-name">{s.name}</span>
              <span className="cs-role">{s.role === "owner" ? "Owner" : s.role === "admin" ? "Admin" : "Member"}</span>
            </label>
            {v && (
              <div className="cs-server-body">
                <div className="cs-group" role="radiogroup" aria-label={`What it may do in ${s.name}`}>
                  {(
                    [
                      { access: "read", label: "Read", help: "See the apps and their data and History." },
                      { access: "act", label: "Read and act", help: "Also use the apps as you: add, vote, change things." },
                    ] as const
                  ).map((o) => (
                    <label key={o.access} className={`cs-option${v.access === o.access ? " on" : ""}`}>
                      <input type="radio" name={`${id}-access`} checked={v.access === o.access} onChange={() => set(s.id, { access: o.access })} />
                      <span>
                        <span className="cs-option-label">{o.label}</span>
                        <span className="cs-option-help">{o.help}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="cs-apps">
                  <label className="cs-check">
                    <input type="radio" name={`${id}-apps`} checked={v.apps === "all"} onChange={() => set(s.id, { apps: "all" })} />
                    All apps, including ones added later
                  </label>
                  <label className="cs-check">
                    <input type="radio" name={`${id}-apps`} checked={v.apps !== "all"} onChange={() => set(s.id, { apps: s.apps.slice(0, 1).map((a) => a.slug) })} disabled={!s.apps.length} />
                    Only some apps
                  </label>
                  {v.apps !== "all" && (
                    <ul className="cs-app-list">
                      {s.apps.map((a) => {
                        const list = v.apps as string[];
                        const on = list.includes(a.slug);
                        return (
                          <li key={a.slug}>
                            <label className="cs-check cs-app">
                              <input type="checkbox" checked={on} onChange={() => set(s.id, { apps: on ? list.filter((x) => x !== a.slug) : [...list, a.slug] })} />
                              <span className="cs-app-icon" aria-hidden="true">{a.icon}</span>
                              <span className="cs-app-text">
                                <span className="cs-app-name">{a.name}</span>
                                <span className="cs-app-desc">{a.description}</span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <label className="cs-check cs-build">
                  <input type="checkbox" checked={!!v.build} onChange={(e) => set(s.id, { build: e.target.checked })} />
                  <span>
                    <span className="cs-option-label">Build apps</span>
                    <span className="cs-option-help">Read blooming threads' code and add drafts as you, with your own AI. Publishing still needs you to be the app's owner or editor.</span>
                  </span>
                </label>
                <div className="cs-section">
                  <div className="cs-option-label">Channel messages</div>
                  <div className="cs-segments" role="radiogroup" aria-label={`Channel messages in ${s.name}`}>
                    {(
                      [
                        ["off", "None"],
                        ["read", "Read"],
                        ["send", "Read and send"],
                      ] as [MessageAccess, string][]
                    ).map(([m, label]) => (
                      <label key={m} className={`cs-check${(v.messages ?? "off") === m ? " on" : ""}`}>
                        <input type="radio" name={`${id}-messages`} checked={(v.messages ?? "off") === m} onChange={() => set(s.id, { messages: m })} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {(v.messages ?? "off") !== "off" && (
                    <>
                      <span className="cs-option-help">{v.messages === "send" ? "Messages it sends show as yours, marked with its name." : "Read channels and their threads as you."}</span>
                      <SomeOrAll name={`${id}-channels`} all="All channels, including new ones" some="Only some channels" items={(s.channels ?? []).map((c) => ({ id: c.id, name: `#${c.name}` }))} value={v.channels ?? "all"} onChange={(channels) => set(s.id, { channels })} />
                    </>
                  )}
                </div>
                <div className="cs-section">
                  <div className="cs-option-label">DMs</div>
                  <div className="cs-segments" role="radiogroup" aria-label={`DMs in ${s.name}`}>
                    {(
                      [
                        ["off", "None"],
                        ["read", "Read"],
                      ] as [DmAccess, string][]
                    ).map(([d, label]) => (
                      <label key={d} className={`cs-check${(v.dms ?? "off") === d ? " on" : ""}`}>
                        <input type="radio" name={`${id}-dms`} checked={(v.dms ?? "off") === d} onChange={() => set(s.id, { dms: d })} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {(v.dms ?? "off") === "read" && (
                    <>
                      <span className="cs-option-help">Read only: AIs never send in DMs. The people in a DM see that your AI can read it, and anyone can turn that off for their DMs.</span>
                      <SomeOrAll name={`${id}-dmpeople`} all="DMs with anyone" some="Only DMs with some people" items={s.people ?? []} value={v.dmPeople ?? "all"} onChange={(dmPeople) => set(s.id, { dmPeople })} />
                    </>
                  )}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Some server is chosen with "only some" apps, channels or people but none ticked. */
export const scopesIncomplete = (v: GrantServerScope[]) =>
  v.some(
    (s) =>
      (s.apps !== "all" && !s.apps.length) ||
      ((s.messages ?? "off") !== "off" && s.channels !== undefined && s.channels !== "all" && !s.channels.length) ||
      ((s.dms ?? "off") !== "off" && s.dmPeople !== undefined && s.dmPeople !== "all" && !s.dmPeople.length),
  );
